import { fromBaseRecord } from './from-base-context';
import { readItemField } from './item-field-reader';
import type { BaseEnvelope } from './base-patch';

export interface InventorySharedCatalog { version: string; metadata: unknown; base: Record<string, unknown> }

// Numbers count dictionary levels before a complete row/value replaces its old
// identity. Strings name SELECT row identities; '' denotes an id-list closure.
type MergePolicy = number | string | { readonly [key: string]: MergePolicy };
const metadataPolicy: MergePolicy = {
    layerCatalog: 1, stageSources: 1, shellRows: 1, shellRegistries: 2,
    definitionCatalog: { staticIds: '', referenceNames: 1, identityRows: 1 },
    definitionMedia: { audioTaxonomy: 2, statusBases: 1 },
    definitionAssets: { paidMeshIds: '', vfxRows: 'ability_vfx_item_id', lookSources: 1, slotDefaults: 1, slots: 'item_id',
        magicCatalog: { textures: { assets: 1 }, audioRows: 'audio_id' } },
};
const packagePolicy: MergePolicy = {
    ...metadataPolicy, inventory_metadata: metadataPolicy, catalog: 1, dependency_revs: 1, random_table_rows: 1, random_table_ids: '',
    enchantment_templates: 'enchantment_template_id', enchantment_template_enchantments: 'enchantment_id',
    monster_templates: 'monster_template_id', monster_template_layers: 'monster_template_layer_id',
    missing_definition_ids: '', missing_enchantment_template_ids: '', missing_monster_template_ids: '',
    definition_videos: { lookupIds: '', rows: 'video_id' },
    picture_lookup_ids: '', picture_sources: 'picture_id',
};

export function inventoryCatalogScope(shared: InventorySharedCatalog, scope: Record<string, unknown>): InventorySharedCatalog {
    return { ...shared, base: merge(shared.base, scope, packagePolicy) as Record<string, unknown> };
}

export function mergeInventoryMetadata<T extends object>(shared: T, selected: unknown): T {
    return (fromBaseRecord(selected) ? merge(shared, selected, packagePolicy) : { ...shared }) as T;
}

/** Merge only declared source closures; definitions and ordinary lists are authoritative. */
function merge(shared: unknown, tree: unknown, policy?: MergePolicy): unknown {
    if (policy === undefined) return tree;
    if (typeof policy === 'string') {
        if (!Array.isArray(shared) || !Array.isArray(tree)) return tree;
        const identity = (row: unknown): string => {
            const id = policy && fromBaseRecord(row) ? readItemField(row, policy) : null;
            return typeof id === 'string' && id !== '' ? `id:${id}` : `json:${JSON.stringify(row)}`;
        };
        return [...new Map([...shared, ...tree].map(row => [identity(row), row])).values()];
    }
    // PHP serializes empty metadata dictionaries as []. This exception ends at
    // the dictionary boundary, before authoritative definition/registry rows.
    if (Array.isArray(tree) && tree.length === 0 && fromBaseRecord(shared)) return { ...shared };
    if (fromBaseRecord(tree)) {
        const out = { ...fromBaseRecord(shared) ? shared : {} };
        for (const [key, value] of Object.entries(tree)) out[key] = merge(out[key], value,
            typeof policy === 'number' ? policy > 1 ? policy - 1 : undefined : policy[key]);
        return out;
    }
    return tree;
}

/** The exact inverse of mergeInventoryMetadata(selected, held): the part of `selected` that `held` does not already carry, judged by the same
 *  policy (definition ids, registry keys, row identities, id lists; a leaf the holder has is the holder's). merge(held, subtract(selected, held))
 *  therefore equals merge(selected, held) — what a relocated member's compile merged — without shipping the holder's own package again. */
export function subtractInventoryMetadata(selected: unknown, held: unknown): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    absorbInventoryMetadata(out, selected, held);
    return out;
}
/** Accumulate what `held` lacks from several packages into ONE mutable union, in a single pass per package and without copying what is already
 *  there: an entry present in `held` or already in `target` is skipped (first contributor wins at a leaf, as the holder would). Ten relocated
 *  members from one template add their shared closure once; a fold of pairwise merges copied the growing union per member instead. */
export function absorbInventoryMetadata(target: Record<string, unknown>, selected: unknown, held: unknown, owned: WeakSet<object> = new WeakSet()): void {
    owned.add(target);
    if (fromBaseRecord(selected)) absorb(target, selected, held, packagePolicy, owned);
}
/** `owned`: the containers THIS accumulation allocated (the caller keeps one set for all contributions to one target; a call without one
 *  owns only what it allocates itself, so a container that reached the target through an earlier call is treated as borrowed). Everything
 *  else under `target` is a contributor's object, shared by reference until a later contributor has to write into it: then only that branch
 *  is copied (shallow, at the written level), never a whole package, and the copy is owned from there on. Contributor packages — and an
 *  accumulator later used as a contributor elsewhere — are never mutated. */
const lacks = (held: unknown, value: unknown): boolean => held === undefined || Array.isArray(held) && held.length === 0 && fromBaseRecord(value);
function writable(target: Record<string, unknown>, key: string, owned: WeakSet<object>): Record<string, unknown> {
    const current = target[key] as Record<string, unknown>;
    if (owned.has(current)) return current;
    const copy = { ...current };
    owned.add(copy); target[key] = copy;
    return copy;
}
function absorb(target: Record<string, unknown>, tree: Record<string, unknown>, held: unknown, policy: MergePolicy, owned: WeakSet<object>): void {
    const holder = fromBaseRecord(held) ? held : undefined;
    for (const [key, value] of Object.entries(tree)) {
        const child: MergePolicy | undefined = typeof policy === 'number' ? policy > 1 ? policy - 1 : undefined : typeof policy === 'object' ? policy[key] : undefined;
        const have = holder?.[key], own = target[key];
        // A merged base borrows the holder's own objects for everything the member did not bring: identical means nothing to add.
        if (value === have) continue;
        if (lacks(have, value)) {
            if (own === undefined) target[key] = value; // shared by reference; copied only if a later contributor extends it
            else if (typeof child === 'string') { if (Array.isArray(own) && Array.isArray(value)) target[key] = unionRows(own, value, child); }
            else if (child !== undefined && fromBaseRecord(own) && fromBaseRecord(value) && own !== value) absorb(writable(target, key, owned), value, undefined, child, owned);
            continue;
        }
        if (child === undefined) continue;
        if (typeof child === 'string') {
            if (!Array.isArray(value) || !Array.isArray(have)) continue;
            const rows = unionRows(have, value, child).slice(have.length);
            if (rows.length) target[key] = Array.isArray(own) ? unionRows(own, rows, child) : rows;
            continue;
        }
        if (!fromBaseRecord(value) || !fromBaseRecord(have)) continue;
        if (fromBaseRecord(own)) { absorb(writable(target, key, owned), value, have, child, owned); continue; }
        const into: Record<string, unknown> = {};
        owned.add(into);
        absorb(into, value, have, child, owned);
        if (Object.keys(into).length) target[key] = into;
    }
}
/** The row union merge() builds for a string policy: identities already present keep their first row. */
function unionRows(first: readonly unknown[], second: readonly unknown[], policy: string): unknown[] {
    const identity = (row: unknown): string => {
        const id = policy && fromBaseRecord(row) ? readItemField(row, policy) : null;
        return typeof id === 'string' && id !== '' ? `id:${id}` : `json:${JSON.stringify(row)}`;
    };
    const seen = new Set(first.map(identity)), out = [...first];
    for (const row of second) { const key = identity(row); if (!seen.has(key)) { seen.add(key); out.push(row); } }
    return out;
}

export function attachInventoryCatalog(envelope: BaseEnvelope, shared: InventorySharedCatalog): BaseEnvelope {
    if (envelope.catalog_version !== shared.version) throw new Error('inventory-catalog-version');
    return { ...envelope, metadata: shared.metadata, base: merge(shared.base, envelope.base, packagePolicy) as BaseEnvelope['base'] };
}
